
import { Vue, Options } from "vue-class-component";
import { Ref } from "vue-property-decorator";
import { Watch } from "vue-property-decorator";
import { RouteLocation } from "vue-router";
import {
  GetApplicantProfile,
  ApplicantApi,
  SystemApi,
  ClientApi,
  UploadItemEnum,
  RoleName,
  AsExportTypeEnum,
  ApplicantStatsEnum,
  Department,
  Occupation
} from "../../autogen";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import Table from "../molecules/Table.vue";
import UIkit from "uikit";
import ApplicantForm from "../organisms/ApplicantForm.vue";
import SearchField from "../atoms/SearchField.vue";
import Router from "../../router";
import FileSelectorButton from "../molecules/FileSelectorButton.vue";
import Store from "../../store";
import { InstructionLevel } from "../../types";
import Multiselect from "@vueform/multiselect";
import moment from "moment-timezone";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";
import { ApplicantStatusEnum } from "../../types";
type FormValues = {
  name?: string;
  departments?: Department[];
  occupations?: Occupation[];
  startDate?: Date;
  examStartDate?: Date;
  endDate?: Date;
  examEndDate?: Date;
};

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    ApplicantForm,
    SearchField,
    FileSelectorButton,
    Multiselect,
    Datepicker
  }
})
export default class Applicants extends Vue {
  private readonly storeInstance = Store.getInstance();
  applicants: (GetApplicantProfile & {
    name: string;
    positionName?: string;
  })[] = [];
  applicant: GetApplicantProfile | null = null;
  formKey = 0;
  header = [
    { title: "名前", sortable: true, key: "name", width: "expand" },
    { title: "採用区分", sortable: true, key: "career_name", width: "shrink" },
    { title: "ステータス", sortable: true, key: "status_name", width: "shrink" },
    { title: "部署", sortable: true, key: "department_name", width: "expand" },
    { title: "職種", sortable: true, key: "occupation_name", width: "expand" },
    { title: "最終受検日", key: "last_exam_date", width: 90 },

    {
      title: "受検結果",
      type: "button",
      key: "result",
      width: 120,
      disabled: (row: GetApplicantProfile) => {
        return !this.appropriateResults || !row.lastExamDate;
      }
    },
    { title: "詳細", type: "button", key: "edit", width: 100, disabled: () => !this.isReadableApplicant }
  ];
  filterColumns = ["name", "department_name", "occupation_name", "status_name", "career_name", "last_exam_date"];
  @Ref("modal") modal!: HTMLElement;
  @Ref("form") form!: HTMLElement;
  query = "";
  current_status = "";
  availableRegistration = false;
  isReadableApplicant = false;
  isEditableApplicant = false;
  appropriateResults = false;

  departments = {};
  occupations = {};
  occValues: [] = [];
  posValues: [] = [];
  offset?: string;
  totalCount = "-";
  FormValues: FormValues = { departments: [], occupations: [] };
  locale = ja;
  selectedStatusName: string = "";
  statusDict: { [key: string]: string } = {
    all: "全て",
    [ApplicantStatusEnum.Screening]: "選考中",
    [ApplicantStatusEnum.Hired]: "採用",
    [ApplicantStatusEnum.NotHired]: "不採用",
    [ApplicantStatusEnum.Joined]: "入社"
  };
  async beforeMount() {
    if (!this.storeInstance.getRole().id) await this.storeInstance.getRoleApi();
    this.availableRegistration = this.$role.isStrongerThan(RoleName.ClientAdmin);
    this.appropriateResults = this.$role.isReadable("applicantStats");
    this.isReadableApplicant = this.$role.isReadable("applicant");
    this.isEditableApplicant = this.$role.isEditable("applicant");
    const commonCondtions = {
      mode: "tags",
      label: "name",
      trackBy: "name",
      valueProp: "id",
      max: 5,
      closeOnSelect: false,
      searchable: true,
      noResultsText: "候補がありません"
    };
    this.departments = Object.assign({}, commonCondtions, { options: this.storeInstance.getShortDepartmentsList({}) });
    this.occupations = Object.assign({}, commonCondtions, { options: this.storeInstance.getOccupations() });
    this.setSelectedStatusName();
  }
  async getTableData() {
    if (this.offset === null) {
      return [];
    }
    const response = await new ApplicantApi().getApplicants(
      Object.assign({
        limit: "100",
        offset: this.offset ? this.offset : undefined,
        name: this.FormValues.name ? this.FormValues.name : undefined,
        status: this.current_status ? this.current_status : undefined,
        departments: this.FormValues.departments?.length ? this.FormValues.departments : undefined,
        occupations: this.FormValues.occupations?.length ? this.FormValues.occupations : undefined,
        examStartDate: this.FormValues.startDate ? moment(this.FormValues.startDate).format("YYYY-MM-DD") : undefined,
        examEndDate: this.FormValues.endDate ? moment(this.FormValues.endDate).format("YYYY-MM-DD") : undefined
      })
    );
    this.totalCount = response.data.totalCount || "-";
    this.offset = response.data.offset;
    if (!response.data.lists || !response.data.lists.length) return [];
    return response.data.lists.map(d =>
      Object.assign(
        {
          name: `${d.lastName} ${d.firstName}`,
          career_name: d.career?.name,
          status_name: d.status?.name,
          department_name: d.department?.name || "-",
          occupation_name: d.occupation?.name || "-",
          last_exam_date: d.lastExamDate ? moment(d.lastExamDate).format("YYYY/MM/DD") : "-"
        },
        d
      )
    );
  }
  onAddButtonClick() {
    this.applicant = null;
    this.prepareForm();
  }
  private closeForm() {
    UIkit.modal(this.modal).hide();
  }
  private prepareForm() {
    this.formKey = Date.now();
    UIkit.modal(this.modal).show();
  }
  onSubmitted() {
    this.closeForm();
    this.onApplicantSearch();
  }
  onApplicantSearch() {
    const table = this.$refs.tableComponent as Table;
    if (table) {
      this.offset = undefined;
      table.reset();
    }
  }
  @Watch("$route")
  async onRouteChanged(to: RouteLocation) {
    const path = to.path.split("/").filter(Boolean);
    if (path.slice(-1)[0] === "applicants") {
      this.current_status = this.$route.query.status as string;
      this.onApplicantSearch();
      this.setSelectedStatusName();
    }
  }
  async onColumnClick(location: { row: number; col: string; el: HTMLElement; rowData: GetApplicantProfile }) {
    if (location.col === "result") {
      this.$router.push({
        name: "ApplicantStats",
        params: { clientId: this.$route.params["clientId"], peopleId: location.rowData.id }
      });
    } else if (location.col === "edit") {
      const response = await new ApplicantApi().getApplicant({
        applicantId: location.rowData.id
      });
      this.applicant = Object.assign(response.data, {
        departmentId: response.data.department ? response.data.department.id : "",
        occupationId: response.data.occupation ? response.data.occupation.id : "",
        contractType: response.data.contractType?.code || "",
        career: response.data.career?.code,
        status: response.data.status?.code
      });

      this.prepareForm();
    }
  }
  onConverted(data: { employeeId: string }) {
    UIkit.modal(this.modal).hide();
    Router.push({
      path: `/clients/${this.$route.params["clientId"]}/departments/all/employees`,
      query: { id: data.employeeId }
    });
  }
  async onSelectExcel(file: File) {
    Store.getInstance().setIsLoading(true);
    try {
      await new SystemApi().uploadExcel({
        file,
        item: UploadItemEnum.Applicants
      });
      Store.getInstance().setInstructionContent(InstructionLevel.Info, "登録を受け付けました");
      this.onApplicantSearch();
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  async onResultsExportButtonClick() {
    Store.getInstance().setIsLoading(true);
    const encoding = require("encoding-japanese");
    const status = this.$route.query.status as string;
    const params = status
      ? { type: AsExportTypeEnum.ApplicantWithStatus, applicantStatus: status as ApplicantStatsEnum }
      : { type: AsExportTypeEnum.ApplicantAll };
    const response = await new SystemApi().asexport(params);
    const str_array = encoding.stringToCode(response.data);
    const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
    const uint8_array = new Uint8Array(sjis_array);

    const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));
    const anchor = document.createElement("a");
    Store.getInstance().setIsLoading(false);
    anchor.href = url;
    anchor.setAttribute("download", `受検結果ダウンロード（応募者）_${new Date().toISOString()}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  async onDownloadTemplateFile(event: Event) {
    event.preventDefault();
    Store.getInstance().setIsLoading(true);
    try {
      const response = await new ClientApi().getXlsTemplate({ peopleType: "applicant" }, { responseType: "arraybuffer" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Applicant-import-Template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, "エラーが発生しました");
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  resetConditions() {
    this.FormValues = { departments: [], occupations: [] };
    this.onApplicantSearch();
  }
  setSelectedStatusName() {
    this.current_status = this.current_status ? this.current_status : (this.$route.query.status as string);
    this.selectedStatusName = this.current_status in this.statusDict ? this.statusDict[this.current_status] : "全て";
  }
}
